import 'twin.macro'

import ContainerWithSteps from 'components/UI/Container/ContainerWithSteps'
import SigningLoader from 'components/views/Biometry/BiometryProcessing'
import DocumentsGenerating from 'components/views/DocumentsSign/DocumentsGenerating'
import DocumentsList from 'components/views/DocumentsSign/DocumentsList'
import DocumentsSignHeader from 'components/views/DocumentsSign/DocumentsSignHeader'
// import LoanAgreementSign from 'components/views/DocumentsSign/LoanAgreementSign'
import SetPayDay from 'components/views/DocumentsSign/SetPayDay'
import { useAppSelector } from 'hooks'
import { useSigningLoader } from 'hooks/useSigningLoader'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useGetApplicationStatusQuery } from 'redux/api'
import { useSetScoringPaydayMutation } from 'redux/api/scoringApi'
import { getCreditParamsSelector, getStatusSelector } from 'redux/store/slices/statusSlice'
import { getCurrentFlowId } from 'redux/store/slices/userSlice'
import { getNotificationsSelector } from 'redux/store/slices/wsSlice'
import { ApplicationEvents } from 'share/enums/events'
import { FLOW_STEPS, Nullable } from 'share/types'
import tw from 'twin.macro'
import { getWsStepStatus, getWsStepStatuses } from 'utils'
import Emitter from 'utils/emitter'

const disabledPickerDays = (day: number) => {
  return day === 30 || day === 25 || day % 5 !== 0
}

const LOADER_QUERY_PARAM = 'show-signing-loader'

const COMMON_DOCS_SIGNING_STEPS = [
  FLOW_STEPS.SIGN_DOCS_AITU,
  FLOW_STEPS.SIGN_DOCS_AITU_DAMU,
  FLOW_STEPS.SIGN_DOCS_AITU_PLEDGE,
  FLOW_STEPS.BLOCKCHAIN_CREATE_RECORD,
  FLOW_STEPS.ACQUAINTANCE_WITH_DOCUMENTS
]

// const PLEDGE_AGREEMENT_SIGNING_STEPS = [
//   FLOW_STEPS.DOCUMENT_SIGNING_AND_VERIFICATION,
//   FLOW_STEPS.SEND_PLEDGE_AGREEMENTS_CLIENT_SIGNATURE_TO_CRED_ADMIN
// ]

const DocumentsSignPage = () => {
  // Selectors
  const currentFlowId = useAppSelector(getCurrentFlowId)
  const { pay_day } = useAppSelector(getCreditParamsSelector)
  const { current_step, next_step } = useAppSelector(getStatusSelector)
  const { SIGN_DOCS_AITU, SIGN_DOCS_AITU_DAMU, SIGN_REPAYMENT_SCHEDULE_WITH_AITU } =
    useAppSelector(getNotificationsSelector)

  // Queries
  useGetApplicationStatusQuery(currentFlowId as string, {
    skip: !currentFlowId
  })

  // Mutations
  const [setPayday, { isLoading: isSetPayDayLoading }] = useSetScoringPaydayMutation()

  // State
  const [repaymentDay, setRepaymentDay] = useState<Nullable<number>>(null)

  // Hooks
  const [showSigningLoader, removeLoaderSearchParam] = useSigningLoader(LOADER_QUERY_PARAM)

  // Computed values
  const isSigningExternal = COMMON_DOCS_SIGNING_STEPS.includes((next_step || current_step?.step) as FLOW_STEPS)
  // const isSigningInNcalayer = PLEDGE_AGREEMENT_SIGNING_STEPS.includes((next_step || current_step?.step) as FLOW_STEPS)

  const isDocGeneratingDone = isSigningExternal
  // || isSigningInNcalayer

  const componentToRender = useMemo(() => {
    if (isSigningExternal) {
      return <DocumentsList twStyle={tw`mt-5`} />
    }
    // else if (isSigningInNcalayer) {
    // return <LoanAgreementSign twStyle={tw`mt-5`} />
    // }
  }, [isSigningExternal])

  // Handlers
  const handleSetPayday = async () => {
    try {
      await setPayday({ id: currentFlowId as string, pay_day: repaymentDay as number })
      Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
    } catch (error) {
      console.log(error)
    }
  }

  // Effects
  useEffect(() => {
    if (
      getWsStepStatus(SIGN_DOCS_AITU) === 'DONE' ||
      getWsStepStatus(SIGN_DOCS_AITU_DAMU) === 'DONE' ||
      getWsStepStatus(SIGN_REPAYMENT_SCHEDULE_WITH_AITU) === 'DONE'
    ) {
      removeLoaderSearchParam()
      setTimeout(() => {
        Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
      }, 300)
    } else if (getWsStepStatuses(SIGN_DOCS_AITU, SIGN_DOCS_AITU_DAMU, SIGN_REPAYMENT_SCHEDULE_WITH_AITU) === 'FAILED') {
      removeLoaderSearchParam()
    }
  }, [SIGN_DOCS_AITU, SIGN_DOCS_AITU_DAMU, SIGN_REPAYMENT_SCHEDULE_WITH_AITU, removeLoaderSearchParam])

  return (
    <ContainerWithSteps>
      <div tw='flex flex-col px-4 pt-6 md:px-0 md:pt-0'>
        {showSigningLoader ? (
          <SigningLoader />
        ) : (
          <Fragment>
            <DocumentsSignHeader />
            <SetPayDay
              payDay={pay_day}
              repaymentDay={repaymentDay}
              isLoading={isSetPayDayLoading}
              onRepaymentDayChange={setRepaymentDay}
              disabledPickerDays={disabledPickerDays}
              onSetPayDay={handleSetPayday}
            />
            {isDocGeneratingDone ? componentToRender : pay_day ? <DocumentsGenerating twStyle={tw`mt-5`} /> : null}
          </Fragment>
        )}
      </div>
    </ContainerWithSteps>
  )
}

export default DocumentsSignPage
